"use client";

import React, { useEffect, useRef, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const reasons = [{
  title: "Direct Purchasing Power",
  content: "We leverage significant capital and deep-rooted relationships with major scrap yards across Canada and the US. This allows us to secure high-volume HMS and shredded materials directly at the source, ensuring competitive pricing and consistent supply."
}, {
  title: "Seamless Brokerage Expertise",
  content: "Our trading desks in Kelowna and New York navigate the complexities of global commodity markets. We provide transparent, real-time pricing and professional transaction management, bridging the gap between North American yards and international mills."
}, {
  title: "Logistics & Shipping Precision",
  content: "From inland rail logistics to specialized ocean freight, we coordinate every step of the global supply chain. Our team ensures rigorous quality inspections and reliable delivery to key international destinations, including İskenderun and beyond."
}, {
  title: "Global Export Specialization",
  content: "We specialize in the bulk export of HMS 1/2 (80/20, 90/10), shredded scrap, and P&S materials. Our operations are built to meet the high-tonnage demands of the world's most sophisticated steel mills with uncompromising reliability."
}];
export default function WhyDiecloudIsTheRightPartner() {
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="221619" id="the-strategic-edge" className="bg-background py-20 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {}
          <div ref={contentRef} className={`lg:col-span-7 flex flex-col gap-8 transition-all duration-1000 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-4">
              <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight tracking-tight">
                The Strategic Edge in Global Metal Trading
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl font-default">
                Arem Metals combines industrial-scale sourcing with modern logistics precision. We move serious volume from North American yards to global steel mills with absolute reliability.
              </p>
            </div>

            <div className="bg-card rounded-none shadow-md border border-border/40 p-2 md:p-4">
              <Accordion type="single" collapsible className="w-full">
                {reasons.map((reason, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="px-4 py-2 border-border/60 last:border-0">
                    <AccordionTrigger className="text-base md:text-lg font-bold font-default text-foreground hover:text-primary transition-colors text-left uppercase tracking-wider">
                      {reason.title}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed font-default pt-2 pb-4">
                      {reason.content}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`lg:col-span-5 relative transition-all duration-1000 delay-300 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="relative rounded-none overflow-hidden shadow-2xl border border-border/20 group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1639953/img/it_consultant_meeting_with_client.png" alt="Arem Metals trading team coordinating global scrap metal logistics" className="w-full h-full object-cover aspect-[4/5] grayscale hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
              {}
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 via-transparent to-transparent mix-blend-overlay pointer-events-none" />
              
              {}
              <div className="absolute bottom-6 left-6 right-6 bg-background/90 backdrop-blur-md border border-border/40 rounded-none p-4 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 size-12 bg-primary/10 rounded-none flex items-center justify-center border border-primary/20">
                    <svg className="size-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-mono font-bold text-foreground text-sm uppercase tracking-widest">Global Scale</p>
                    <p className="text-xs text-muted-foreground font-mono">High-Volume Commodity Trading</p>
                  </div>
                </div>
              </div>
            </div>
            
            {}
            <div className="absolute -z-10 top-1/2 -right-12 -translate-y-1/2 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
          </div>

        </div>
      </div>
    </section>;
}